import React, { useEffect, useState } from "react";
import {
  X,
  Share,
  Plus,
  Smartphone,
  Monitor,
  MoreVertical,
  Download,
  Globe,
} from "lucide-react";

const KEY = "a2hs_dismissed_v1";
const DELAY_MS = 2200;
const SUPPRESS_DAYS = 14;

function detectPlatform() {
  if (typeof window === "undefined") return null;
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIPadOS =
    /Macintosh/.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isIPadOS || isAndroid;

  const isEdge = /Edg\//.test(ua);
  const isOpera = /OPR\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge && !isOpera;
  const isFirefox = /Firefox\//.test(ua);
  // Safari desktop: contains "Safari" but not Chrome/Edge tokens, and is not mobile
  const isSafari =
    /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Edg\//.test(ua);
  const isSafariDesktop = isSafari && !isMobile;

  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;

  return {
    isIOS: isIOS || isIPadOS,
    isAndroid,
    isMobile,
    isChrome,
    isEdge,
    isFirefox,
    isSafariDesktop,
    isStandalone,
    isDesktop: !isMobile,
  };
}

function describePlatform(p) {
  if (!p) return "your browser";
  if (p.isIOS) return "iPhone / iPad";
  if (p.isAndroid) return "Android";
  if (p.isEdge) return "Microsoft Edge";
  if (p.isChrome) return "Google Chrome";
  if (p.isSafariDesktop) return "Safari";
  return "your browser";
}

export default function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    let p = detectPlatform();

    // Forced preview mode: ?install=1 | ios | android | chrome | edge | safari
    const params =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null;
    const forceVal = params?.get("install");
    const force = !!forceVal;

    if (force) {
      const v = forceVal;
      p = {
        isIOS: v === "ios" || v === "1",
        isAndroid: v === "android",
        isMobile: v === "ios" || v === "1" || v === "android",
        isChrome: v === "chrome",
        isEdge: v === "edge",
        isFirefox: false,
        isSafariDesktop: v === "safari",
        isDesktop: v === "chrome" || v === "edge" || v === "safari",
        isStandalone: false,
      };
    }
    setPlatform(p);

    if (!force) {
      if (!p || p.isStandalone) return; // already installed
      // Skip Firefox (no install support) — but allow other desktops too
      if (p.isFirefox) return;

      try {
        const raw = localStorage.getItem(KEY);
        if (raw) {
          const ts = parseInt(raw, 10);
          if (!Number.isNaN(ts) && Date.now() - ts < SUPPRESS_DAYS * 86400_000) {
            return;
          }
        }
      } catch {
        /* ignore */
      }
    }

    // Capture native install events from Chromium browsers (Chrome / Edge / Android Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const t = setTimeout(() => setOpen(true), force ? 200 : DELAY_MS);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const installNative = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* ignore */
    }
    setDeferred(null);
    dismiss();
  };

  if (!open || !platform) return null;

  const headerIcon = platform.isMobile ? Smartphone : Monitor;
  const HeaderIcon = headerIcon;
  const headline = platform.isMobile
    ? "Save it to your home screen"
    : "Install it on your desktop";
  const intro = platform.isMobile
    ? "Add the Tradie Calculator to your phone and it'll open like an app — one tap, no browser bars, works on the job site."
    : `Install the Tradie Calculator as a desktop app in ${describePlatform(
        platform,
      )}. It launches in its own window — no tabs, no clutter.`;

  const browserName = describePlatform(platform);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      data-testid="a2hs-prompt"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={dismiss}
        aria-hidden="true"
      />

      <div className="relative w-full sm:max-w-md bg-[var(--surface)] border-t sm:border border-[var(--border)] mx-0 sm:mx-4">
        <div className="hi-vis-stripe h-1.5 w-full" />
        <div className="p-6">
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-3 right-3 text-zinc-400 hover:text-white p-2"
            aria-label="Close"
            data-testid="a2hs-close"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[var(--primary)] flex items-center justify-center shrink-0">
              <HeaderIcon size={20} className="text-black" />
            </div>
            <div>
              <div className="label-mono">Pro tip · {browserName}</div>
              <h3 className="font-display text-xl tracking-tight mt-1">
                {headline}
              </h3>
            </div>
          </div>

          <p className="text-sm text-zinc-300 mt-4 leading-relaxed">{intro}</p>

          {/* Native install button (Chrome / Edge / Android Chrome) */}
          {deferred && (
            <div className="mt-5">
              <button
                type="button"
                onClick={installNative}
                className="btn-primary w-full justify-center"
                data-testid="a2hs-install-btn"
              >
                <Download size={14} /> Install app
              </button>
              <div className="text-[10px] font-mono text-zinc-500 mt-2 text-center">
                One tap — no menus
              </div>
            </div>
          )}

          {/* iOS Safari */}
          {!deferred && platform.isIOS && (
            <ol
              className="mt-5 space-y-3 font-mono text-sm"
              data-testid="a2hs-ios-steps"
            >
              <Step n={1}>
                Tap the{" "}
                <Inline>
                  <Share size={14} /> Share
                </Inline>{" "}
                button at the bottom of Safari.
              </Step>
              <Step n={2}>
                Scroll down and tap{" "}
                <Inline>
                  <Plus size={14} /> Add to Home Screen
                </Inline>
                .
              </Step>
              <Step n={3}>
                Tap <span className="text-[var(--primary)]">Add</span> in the top
                right. Done — it's now an app icon.
              </Step>
            </ol>
          )}

          {/* Android Chrome (no native event captured) */}
          {!deferred && platform.isAndroid && (
            <ol
              className="mt-5 space-y-3 font-mono text-sm"
              data-testid="a2hs-android-steps"
            >
              <Step n={1}>
                Tap the{" "}
                <Inline>
                  <MoreVertical size={14} /> menu
                </Inline>{" "}
                (three dots, top right of Chrome).
              </Step>
              <Step n={2}>
                Tap{" "}
                <span className="text-[var(--primary)]">Install app</span> or{" "}
                <span className="text-[var(--primary)]">Add to Home screen</span>
                .
              </Step>
              <Step n={3}>
                Confirm <span className="text-[var(--primary)]">Install</span>.
                The icon shows up next to your apps.
              </Step>
            </ol>
          )}

          {/* Desktop Chrome */}
          {!deferred && platform.isChrome && platform.isDesktop && (
            <ol
              className="mt-5 space-y-3 font-mono text-sm"
              data-testid="a2hs-chrome-steps"
            >
              <Step n={1}>
                Look at the right of Chrome's address bar for the{" "}
                <Inline>
                  <Download size={14} /> install
                </Inline>{" "}
                icon.
              </Step>
              <Step n={2}>
                Click it (or open the{" "}
                <Inline>
                  <MoreVertical size={14} /> menu
                </Inline>{" "}
                → <span className="text-[var(--primary)]">Cast, save, share</span>{" "}
                → <span className="text-[var(--primary)]">Install page as app</span>
                ).
              </Step>
              <Step n={3}>
                Click <span className="text-[var(--primary)]">Install</span>. The
                app opens in its own window with a desktop / Start Menu shortcut.
              </Step>
            </ol>
          )}

          {/* Microsoft Edge */}
          {!deferred && platform.isEdge && (
            <ol
              className="mt-5 space-y-3 font-mono text-sm"
              data-testid="a2hs-edge-steps"
            >
              <Step n={1}>
                In Edge, open the{" "}
                <Inline>
                  <MoreVertical size={14} /> menu
                </Inline>{" "}
                (three dots, top right).
              </Step>
              <Step n={2}>
                Hover over <span className="text-[var(--primary)]">Apps</span>,
                then click{" "}
                <span className="text-[var(--primary)]">Install this site as an app</span>
                .
              </Step>
              <Step n={3}>
                Name it (or keep "Tradie Calculator") and click{" "}
                <span className="text-[var(--primary)]">Install</span>. Pin it to
                taskbar / Start Menu when prompted.
              </Step>
            </ol>
          )}

          {/* Desktop Safari (macOS Sonoma 17.4+) */}
          {!deferred && platform.isSafariDesktop && (
            <ol
              className="mt-5 space-y-3 font-mono text-sm"
              data-testid="a2hs-safari-steps"
            >
              <Step n={1}>
                In the Safari menu bar, click{" "}
                <span className="text-[var(--primary)]">File</span>.
              </Step>
              <Step n={2}>
                Click{" "}
                <Inline>
                  <Plus size={14} /> Add to Dock…
                </Inline>
              </Step>
              <Step n={3}>
                Confirm the name and click{" "}
                <span className="text-[var(--primary)]">Add</span>. It appears in
                your Dock and Launchpad.
              </Step>
              <div className="text-[10px] font-mono text-zinc-500 leading-relaxed pl-9">
                Requires macOS Sonoma 14.4 or newer. On older Macs, just bookmark
                this page in your favourites bar.
              </div>
            </ol>
          )}

          {/* Fallback (other desktops / Firefox) */}
          {!deferred &&
            !platform.isIOS &&
            !platform.isAndroid &&
            !(platform.isChrome && platform.isDesktop) &&
            !platform.isEdge &&
            !platform.isSafariDesktop && (
              <div
                className="mt-5 border border-[var(--border)] p-4 flex items-start gap-3 text-sm"
                data-testid="a2hs-fallback"
              >
                <Globe size={16} className="text-[var(--primary)] shrink-0 mt-0.5" />
                <div className="text-zinc-300 leading-relaxed">
                  Your current browser doesn't support installable apps. Bookmark{" "}
                  <span className="text-[var(--primary)]">calc.nztaxation.nz</span>{" "}
                  for one-click access, or open it in Chrome / Edge / Safari to
                  install as a proper app.
                </div>
              </div>
            )}

          <div className="mt-6 flex items-center gap-2">
            <button
              type="button"
              onClick={dismiss}
              className="btn-ghost text-xs flex-1 justify-center"
              data-testid="a2hs-dismiss"
            >
              Maybe later
            </button>
          </div>

          <div className="mt-4 text-[10px] font-mono text-zinc-500 leading-relaxed">
            By NZ Taxation Ltd · IRD-registered tax agents · Te Atatū, Auckland
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-6 h-6 border border-[var(--primary)] text-[var(--primary)] text-xs font-mono flex items-center justify-center">
        {n}
      </span>
      <span className="text-zinc-200 leading-relaxed">{children}</span>
    </li>
  );
}

function Inline({ children }) {
  return (
    <span className="inline-flex items-center gap-1 text-[var(--primary)]">
      {children}
    </span>
  );
}
